// Auto-extracted tab: controls
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * ControlsTab - extracted from app.jsx
 * Original lines: 8771-8943
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function ControlsTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-slate-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>{controls.length}</div>
                <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Total Controls</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-emerald-900/30 border border-emerald-800" : "bg-emerald-50 border border-emerald-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-emerald-400" : "text-emerald-700"}`}>
                  {controls.filter(c => c.operating_effectiveness === 'effective').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-emerald-300" : "text-emerald-600"}`}>Effective</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-amber-900/30 border border-amber-800" : "bg-amber-50 border border-amber-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-amber-400" : "text-amber-700"}`}>
                  {controls.filter(c => c.operating_effectiveness === 'partially_effective').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-amber-300" : "text-amber-600"}`}>Partial</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-red-900/30 border border-red-800" : "bg-red-50 border border-red-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-red-400" : "text-red-700"}`}>
                  {controls.filter(c => c.operating_effectiveness === 'ineffective').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-red-300" : "text-red-600"}`}>Ineffective</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-700/50 border border-slate-600" : "bg-slate-100 border border-slate-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                  {controls.filter(c => c.operating_effectiveness === 'not_tested').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Not Tested</div>
              </div>
            </div>
            
            {/* Control Testing Schedule Widget */}
            <ControlTestingScheduleWidget 
              controls={controls} 
              darkMode={darkMode}
              onScheduleTest={(control) => {
                setControlModal({ open: true, control: control });
                setToast({ type: 'info', message: `Scheduling test for ${control.name || control.title}` });
                setTimeout(() => setToast(null), 3000);
              }}
            />
            
            {/* Control Library */}
            <Card 
              darkMode={darkMode} 
              title="Control Library" 
              subtitle="Internal controls with testing schedule"
              actions={
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={loadControlData} disabled={controlLoading}>
                    <Icons.Refresh className={controlLoading ? "animate-spin" : ""} />
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setControlModal({ open: true, control: null })}>
                    <Icons.Plus /> Add Control
                  </Button>
                </div>
              }
            >
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <select 
                  value={controlFilter.type} 
                  onChange={e => setControlFilter({...controlFilter, type: e.target.value})}
                  className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-300 text-slate-900"} border`}
                >
                  <option value="all">All Types</option>
                  <option value="preventive">Preventive</option>
                  <option value="detective">Detective</option>
                  <option value="corrective">Corrective</option>
                  <option value="directive">Directive</option>
                </select>
                <select 
                  value={controlFilter.effectiveness} 
                  onChange={e => setControlFilter({...controlFilter, effectiveness: e.target.value})}
                  className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-300 text-slate-900"} border`}
                >
                  <option value="all">All Effectiveness</option>
                  <option value="effective">Effective</option>
                  <option value="partially_effective">Partially Effective</option>
                  <option value="ineffective">Ineffective</option>
                  <option value="not_tested">Not Tested</option>
                </select>
              </div>
              
              {/* Control Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={darkMode ? "bg-slate-800/50" : "bg-gradient-to-r from-slate-50 to-slate-100/80"}>
                    <tr>
                      <th className={`text-left py-3.5 px-4 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Control ID</th>
                      <th className={`text-left py-3.5 px-4 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Name</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Type</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Category</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Effectiveness</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Next Test</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controls
                      .filter(c => controlFilter.type === 'all' || c.control_type === controlFilter.type)
                      .filter(c => controlFilter.effectiveness === 'all' || c.operating_effectiveness === controlFilter.effectiveness)
                      .map(control => (
                      <tr key={control.id} className={`${darkMode ? "border-slate-700/50 hover:bg-slate-800/50" : "border-slate-100 hover:bg-slate-50"} border-b`}>
                        <td className={`py-3 px-2 font-mono ${darkMode ? "text-blue-400" : "text-blue-600"}`}>{control.control_id}</td>
                        <td className="py-3 px-2">
                          <div className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{control.name}</div>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                            {control.control_type}
                          </span>
                        </td>
                        <td className={`py-3 px-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{control.control_category}</td>
                        <td className="py-3 px-2">
                          <Badge variant={
                            control.operating_effectiveness === 'effective' ? 'success' :
                            control.operating_effectiveness === 'partially_effective' ? 'medium' :
                            control.operating_effectiveness === 'ineffective' ? 'critical' : 'default'
                          }>
                            {control.operating_effectiveness?.replace('_', ' ') || 'Not Tested'}
                          </Badge>
                        </td>
                        <td className={`py-3 px-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {control.next_test_date ? new Date(control.next_test_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="xs" onClick={() => setControlDetailModal({ open: true, control })}>
                              <Icons.Eye />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => openControlEdit(control)}>
                              <Icons.Edit />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => setControlTestModal({ open: true, control })}>
                              <Icons.Check /> Test
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => openDeleteModal("control", control)}>
                              <Icons.Trash className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {controls.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-4">
                          <EducationalEmptyState
                            title="No controls documented yet"
                            examinerSays="Show me your control framework. How do you ensure SAR filing deadlines are met? How do you verify CTR accuracy? What controls prevent unauthorized access?"
                            example="SAR Filing Timeline Control - Ensures SARs are filed within 30 days of detection. Daily monitoring dashboard, weekly compliance review, escalation at day 25."
                            templates={[
                              { name: "SAR Filing Timeline", type: "sar_timeline" },
                              { name: "CTR Accuracy Review", type: "ctr_accuracy" },
                              { name: "CDD Refresh Control", type: "cdd_refresh" },
                              { name: "Access Control Review", type: "access_review" },
                            ]}
                            onAddBlank={() => setControlModal({ open: true, control: null })}
                            onSelectTemplate={(t) => setControlModal({ open: true, control: null, template: t.type })}
                            darkMode={darkMode}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
    </>
  );
}

export default ControlsTab;
